import React, { useCallback, useEffect, useMemo, useState } from "react";
import { reportsApi, usersApi, productsApi } from "../../services/api.service";
import { T, SectionLabel, Field, Input, Select, Btn, Modal, SearchableSelect } from "../shared/UI";
import { onlyOnAgents } from "../../utils/normalizers";

const STATUS_CODES = [
  "SC-01", "SC-02", "SC-03", "SC-04", "SC-05",
];

export default function ReportsView() {
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [selfieUrl, setSelfieUrl] = useState("");
  const [reportType, setReportType] = useState("collections"); // collections | activity
  const [filters, setFilters] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      agentId: "",
      productId: "",
      accountNumber: "",
      customerName: "",
      nrrDistrict: "",
      cd: "",
      billingCycle: "",
      fTlName: "",
      tcTlName: "",
      tcName: "",
      statusCode: "",
      amountMin: "",
      amountMax: "",
      distanceMin: "",
      distanceMax: "",
      fromDate: today,
      toDate: today,
      search: "",
    };
  });

  const agentOptions = useMemo(
    () => agents.map(a => ({ id: a.id, name: a.fullName || a.name || a.mobile })),
    [agents]
  );
  const productOptions = useMemo(
    () => products.map(p => ({ id: p.id, name: p.name, cd: p.cd || "" })),
    [products]
  );
  const cdOptions = useMemo(() => {
    const selected = productOptions.find(p => String(p.id) === String(filters.productId));
    if (!selected || !selected.cd) return [];
    return selected.cd.split(",").map(s => s.trim()).filter(Boolean);
  }, [productOptions, filters.productId]);

  const loadAgents = useCallback(async () => {
    try {
      const res = await usersApi.agentsMeta();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setAgents(onlyOnAgents(raw));
    } catch {
      setAgents([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productsApi.list();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setProducts(raw);
    } catch {
      setProducts([]);
    }
  }, []);

  const loadReport = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: meta.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([k, v]) => {
            if (v === "" || v == null) return false;
            if (reportType === "activity" && (k === "distanceMin" || k === "distanceMax")) return false;
            return true;
          })
        ),
      };
      const res = reportType === "activity"
        ? await reportsApi.agentActivity(params)
        : await reportsApi.amountCollections(params);
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const metaRes = res.data?.meta || { page, limit: meta.limit, total: raw.length, totalPages: 1 };
      setRows(raw);
      setMeta({
        page: metaRes.page || page,
        limit: metaRes.limit || meta.limit,
        total: metaRes.total || raw.length,
        totalPages: metaRes.totalPages || 1,
      });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load report";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, meta.limit, reportType]);

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadReport(1); }, [loadReport]);

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = reportType === "activity"
      ? [
          "agentName","agentDesignation","agentDistrict","agentCity","agentArea","monthName","agentMobile","isPresent","productId","productName","date","loginTime","logoutTime",
          "distanceKm","outstationDistanceKm","totalCollectedAmount","totalCollections",
          "loginSelfie","loginAddress","logoutSelfie","logoutAddress",
        ]
      : [
          "agentName","agentDesignation","agentDistrict","agentCity","agentArea","monthName","date","id","agentId","productId","productName","agentMobile","agentEmail",
          "amount","accountNumber","customerName","billingCycle","cd","currBalPos","statusCode","remarks",
          "latitude","longitude","address","distanceKm","homeDistanceKm","isOutstation","selfieImage","createdAt",
        ];
    const escape = (val) => {
      const s = String(val ?? "");
      if (s.includes(",") || s.includes("\"") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [
      headers.join(","),
      ...rows.map(r => headers.map(h => {
        if (h === "isOutstation") {
          return escape(r[h] === 1 ? "Yes" : r[h] === 0 ? "No" : "");
        }
        if (h === "distanceKm" || h === "homeDistanceKm" || h === "outstationDistanceKm") {
          return escape(r[h] != null ? Number(r[h]).toFixed(2) : "");
        }
        return escape(r[h]);
      }).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${reportType}-page-${meta.page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mobile-padding" style={styles.wrap}>
      <div className="reports-toolbar" style={styles.toolbar}>
        <SectionLabel>VISITS REPORT</SectionLabel>
        <div className="reports-tabs" style={styles.tabRow}>
          {[
            { id: "collections", label: "Visits" },
            { id: "activity", label: "Agent Activity" },
          ].map(t => (
            <button
              key={t.id}
              style={{ ...styles.tabBtn, ...(reportType === t.id ? styles.tabActive : {}) }}
              onClick={() => setReportType(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="reports-actions" style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn variant="ghost" onClick={() => loadReport(1)}>Apply</Btn>
          <Btn variant="outline" onClick={exportCsv} disabled={!rows.length}>Export CSV</Btn>
        </div>
        </div>

      <div className="filter-grid" style={styles.filters}>
        <Field label="AGENT">
          <SearchableSelect
            options={[
              { value: "", label: "All agents" },
              ...agentOptions.map(a => ({ value: a.id, label: a.name })),
            ]}
            value={filters.agentId}
            onChange={val => setFilters(f => ({ ...f, agentId: val }))}
            placeholder="All agents"
          />
        </Field>
        <Field label="PRODUCT">
          <Select value={filters.productId} onChange={e => setFilters(f => ({ ...f, productId: e.target.value, cd: "" }))}>
            <option value="">All</option>
            {productOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="ACCOUNT #">
          <Input value={filters.accountNumber} onChange={e => setFilters(f => ({ ...f, accountNumber: e.target.value }))} />
        </Field>
        <Field label="CUSTOMER">
          <Input value={filters.customerName} onChange={e => setFilters(f => ({ ...f, customerName: e.target.value }))} />
        </Field>
        <Field label="CD">
          <Select value={filters.cd} onChange={e => setFilters(f => ({ ...f, cd: e.target.value }))} disabled={!cdOptions.length}>
            <option value="">{cdOptions.length ? "All" : "Select product first"}</option>
            {cdOptions.map(cd => <option key={cd} value={cd}>{cd}</option>)}
          </Select>
        </Field>
        <Field label="BILLING CYCLE">
          <Input value={filters.billingCycle} onChange={e => setFilters(f => ({ ...f, billingCycle: e.target.value }))} />
        </Field>
        <Field label="STATUS CODE">
          <Select value={filters.statusCode} onChange={e => setFilters(f => ({ ...f, statusCode: e.target.value }))}>
            <option value="">All</option>
            {STATUS_CODES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="AMOUNT MIN">
          <Input type="number" value={filters.amountMin} onChange={e => setFilters(f => ({ ...f, amountMin: e.target.value }))} />
        </Field>
        <Field label="AMOUNT MAX">
          <Input type="number" value={filters.amountMax} onChange={e => setFilters(f => ({ ...f, amountMax: e.target.value }))} />
        </Field>
        {reportType === "collections" && (
          <>
            <Field label="DISTANCE MIN (KM)">
              <Input type="number" step="any" min="0" value={filters.distanceMin} onChange={e => setFilters(f => ({ ...f, distanceMin: e.target.value }))} placeholder="km" />
            </Field>
            <Field label="DISTANCE MAX (KM)">
              <Input type="number" step="any" min="0" value={filters.distanceMax} onChange={e => setFilters(f => ({ ...f, distanceMax: e.target.value }))} placeholder="km" />
            </Field>
          </>
        )}
        <Field label="FROM DATE">
          <Input type="date" value={filters.fromDate} onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))} />
        </Field>
        <Field label="TO DATE">
          <Input type="date" value={filters.toDate} onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))} />
        </Field>
        <Field label="SEARCH">
          <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="account/customer/cd" />
        </Field>
        </div>

      {loading && <div style={styles.info}>Loading report…</div>}
      {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}

      <div className="table-wrap" style={styles.tableWrap}>
        {reportType === "collections" ? (
          <table style={styles.table}>
            <thead>
              <tr>
                {["Agent","Designation","District","Agent City","Area","Month","Date","Product","Account","Customer","CD","Amount","Status","Billing","Distance","Home Distance","Outstation","Created","Selfie"].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600 }}>{r.agentName || r.agentId}</div>
                    <div style={{ fontSize: 10, color: T.muted }}>{r.agentMobile}</div>
                  </td>
                  <td className="tdM" style={styles.tdM}>{r.agentDesignation || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentDistrict || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentCity || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentArea || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.monthName || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.date || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.productName || "—"}</td>
                  <td style={styles.td}>{r.accountNumber}</td>
                  <td style={styles.td}>{r.customerName}</td>
                  <td className="tdM" style={styles.tdM}>{r.cd}</td>
                  <td className="tdM" style={styles.tdM}>{r.amount}</td>
                  <td className="tdM" style={styles.tdM}>{r.statusCode}</td>
                  <td className="tdM" style={styles.tdM}>{r.billingCycle}</td>
                  <td className="tdM" style={styles.tdM}>{r.distanceKm != null ? Number(r.distanceKm).toFixed(2) : "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.homeDistanceKm != null ? Number(r.homeDistanceKm).toFixed(2) : "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.isOutstation === 1 ? "Yes" : r.isOutstation === 0 ? "No" : "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.createdAt}</td>
                  <td className="tdM" style={styles.tdM}>
                    {r.selfieImage ? (
                      <Btn size="sm" variant="ghost" onClick={() => setSelfieUrl(r.selfieImage)}>View</Btn>
                    ) : "—"}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td style={styles.empty} colSpan={19}>No records found.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {["Agent","Designation","District","Agent City","Area","Month","Mobile","Present","Product","Date","Login Time","Logout Time","Distance","Outstation","Collected","Collections","Login","Logout"].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} style={styles.tr}>
                  <td style={styles.td}>{r.agentName}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentDesignation || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentDistrict || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentCity || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentArea || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.monthName || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.agentMobile || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{String(r.isPresent) === "1" ? "Yes" : "No"}</td>
                  <td className="tdM" style={styles.tdM}>{r.productName || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.date}</td>
                  <td className="tdM" style={styles.tdM}>{r.loginTime || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.logoutTime || "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.distanceKm != null ? Number(r.distanceKm).toFixed(2) : "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.outstationDistanceKm != null ? Number(r.outstationDistanceKm).toFixed(2) : "—"}</td>
                  <td className="tdM" style={styles.tdM}>{r.totalCollectedAmount}</td>
                  <td className="tdM" style={styles.tdM}>{r.totalCollections}</td>
                  <td className="tdM" style={styles.tdM}>
                    {r.loginSelfie ? (
                      <Btn size="sm" variant="ghost" onClick={() => setSelfieUrl(r.loginSelfie)}>View</Btn>
                    ) : "—"}
                  </td>
                  <td className="tdM" style={styles.tdM}>
                    {r.logoutSelfie ? (
                      <Btn size="sm" variant="ghost" onClick={() => setSelfieUrl(r.logoutSelfie)}>View</Btn>
                    ) : "—"}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td style={styles.empty} colSpan={18}>No records found.</td></tr>
              )}
            </tbody>
          </table>
        )}
        </div>

      <div className="reports-pagination" style={styles.pagination}>
        <Btn size="sm" variant="ghost" disabled={meta.page <= 1} onClick={() => loadReport(meta.page - 1)}>Prev</Btn>
        <span style={styles.pageLabel}>Page {meta.page} of {meta.totalPages}</span>
        <Btn size="sm" variant="ghost" disabled={meta.page >= meta.totalPages} onClick={() => loadReport(meta.page + 1)}>Next</Btn>
      </div>

      <Modal open={!!selfieUrl} onClose={() => setSelfieUrl("")} title="Selfie Image" width={720}>
        {selfieUrl ? (
          <img src={selfieUrl} alt="Collection selfie" style={{ width: "100%", borderRadius: 10 }} />
        ) : null}
      </Modal>
    </div>
  );
}

const styles = {
  wrap: { flex:1, overflowY:"auto", padding:20, display:"flex", flexDirection:"column", gap:16 },
  toolbar: { display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" },
  tabRow: { display:"flex", gap:6 },
  tabBtn: { padding:"5px 10px", borderRadius:6, border:`1px solid ${T.border}`, background:"none", color:T.muted, fontFamily:T.fontMono, fontSize:10, cursor:"pointer" },
  tabActive: { borderColor:T.acc, color:T.acc, background:"rgba(124,106,247,0.08)" },
  filters: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12 },
  info: { fontSize:12, color:T.muted, padding:"6px 4px" },
  tableWrap: { background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12, overflowX:"auto" },
  table: { width:"100%", borderCollapse:"collapse" },
  th: { textAlign:"left", fontFamily:T.fontMono, fontSize:9, letterSpacing:"1.5px", color:T.muted, padding:"6px 10px", borderBottom:`1px solid ${T.border}`, textTransform:"uppercase", whiteSpace:"nowrap" },
  tr: { borderBottom:`1px solid ${T.border}` },
  td: { padding:"8px 10px", fontSize:12, color:T.text, verticalAlign:"middle", whiteSpace:"nowrap" },
  tdM: { padding:"8px 10px", fontSize:11, color:T.muted, verticalAlign:"middle", whiteSpace:"nowrap" },
  empty: { padding:"16px 10px", fontSize:12, color:T.muted, textAlign:"center" },
  pagination: { display:"flex", alignItems:"center", justifyContent:"space-between" },
  pageLabel: { fontSize:11, color:T.muted, fontFamily:T.fontMono },
};
